import type { ConstructionTypeData } from 'gql/graphql';
import { Tabs } from 'uikit';

import * as R from 'ramda';
import React, { useCallback, useMemo } from 'react';

const getLabel = R.prop('name');

export const findAllConstructionTypesFromObjects = <T extends { constructionType: { id: number; name: string } }>({
    workOrderChangeObjectsData,
}: {
    workOrderChangeObjectsData: T[];
}): T['constructionType'][] => {
    const result = workOrderChangeObjectsData?.reduce(
        (
            acc: {
                constructionTypesIds: { [key: number]: boolean };
                constructionTypes: T['constructionType'][];
            },
            obj: T,
        ) => {
            if (!acc.constructionTypesIds[obj?.constructionType?.id]) {
                acc.constructionTypesIds[obj.constructionType.id] = true;

                acc.constructionTypes.push(obj.constructionType);
            }

            return acc;
        },
        {
            constructionTypesIds: {},
            constructionTypes: [],
        },
    );

    const sortedConstructionTypes = result?.constructionTypes?.sort((a, b) => a.name.localeCompare(b.name));

    return sortedConstructionTypes ?? [];
};

export const WorkOrderConstructionTypesFromObjects = <T extends { constructionType: { id: number; name: string } }>({
    workOrderChangeObjectsData,
    onChange,
    activeConstructionTypeId,
}: {
    workOrderChangeObjectsData: T[];
    onChange: (id: ConstructionTypeData) => void;
    activeConstructionTypeId: number | undefined;
}) => {
    const constructionTypes = useMemo(
        () => findAllConstructionTypesFromObjects({ workOrderChangeObjectsData }),
        [workOrderChangeObjectsData],
    );

    const tabChangeHandler = useCallback(
        ({ value }) => {
            onChange(value);
        },
        [onChange],
    );

    const value = useMemo(
        () =>
            constructionTypes.find((constructionType) => constructionType.id === activeConstructionTypeId) ??
            constructionTypes[0],
        [activeConstructionTypeId, constructionTypes],
    );

    return (
        <Tabs
            items={constructionTypes}
            getItemLabel={getLabel}
            onChange={tabChangeHandler}
            value={value}
            fitMode="scroll"
        />
    );
};

WorkOrderConstructionTypesFromObjects.displayName = 'WorkOrderConstructionTypesFromObjects';
